import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import CategoryFilterPills from '../components/CategoryFilterPills';

import { API_BASE, imageUrl } from '../apiConfig';

const CityPage = () => {
  const { cityName } = useParams();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [allStores, setAllStores] = useState([]); // Keep unfiltered stores
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilterSlug, setActiveFilterSlug] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Handle scroll events for scroll-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    // Fetch stores with categories and product counts
    fetch(`${API_BASE}/cities/${cityName}/stores`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setStores(data);
        setAllStores(data); // Store the unfiltered list
        setLoading(false);
      })
      .catch(err => {
        setError('Error loading stores');
        setLoading(false);
        console.error('Error:', err);
      });

    // Fetch available categories for this city
    fetch(`${API_BASE}/cities/${cityName}/categories`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // Map server rows to the shared component's prop shape.
        // The "All Stores" pill is prepended by CategoryFilterPills itself.
        setCategories(
          data.map(c => ({ slug: c.slug, name: c.name, count: c.store_count }))
        );
      })
      .catch(err => {
        console.error('Error loading categories:', err);
        setCategories([]);
      });
  }, [cityName]);

  // Handle category filtering. slug === null means "All Stores".
  const handleFilterChange = (slug) => {
    setActiveFilterSlug(slug);

    if (slug === null) {
      setStores(allStores);
    } else {
      const filteredStores = allStores.filter(store => {
        return store.categories && store.categories.some(cat =>
          cat.slug === slug
        );
      });
      setStores(filteredStores);
    }
  };

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery)}&city=${cityName}&page=1`);
    }
  };

  // Handle smooth scroll to stores
  const scrollToStores = () => {
    const storesSection = document.getElementById('stores-section');
    if (storesSection) {
      const yOffset = -80; // Offset for fixed header
      const y = storesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to truncate text with ellipsis
  const truncateText = (text, limit) => {
    if (!text) return '';
    if (text.length <= limit) return text;
    const lastSpace = text.substring(0, limit).lastIndexOf(' ');
    const truncateAt = lastSpace > 0 ? lastSpace : limit;
    return text.substring(0, truncateAt) + '...';
  };

  // Format city name for display
  const formatCityName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Get store category for badge (use first category or "General")
  const getStoreCategory = (store) => {
    if (store.categories && store.categories.length > 0) {
      return store.categories[0].name;
    }
    return 'General';
  };

  // Get actual product count from database
  const getProductCount = (store) => {
    return store.product_count || 0;
  };

  // Get store image or use default - Fixed to handle absolute URLs
  const getStoreImage = (store, index) => {
    const configuredImage = imageUrl(store.profile_image_url);
    if (configuredImage) return configuredImage;
    // Default images if no profile image
    const images = [
      'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1509440159596-0249088772ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1604654894610-df63bc536371?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    ];
    return images[index % images.length];
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border" role="status" style={{ color: '#FF385C' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading stores...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center text-danger">
          <h3>Oops!</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-outline-primary mt-3">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const styles = {
    header: {
      position: 'sticky',
      top: 0,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #e8e8e8',
      padding: '1rem 0',
      zIndex: 100,
    },
    navContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#FF385C',
      textDecoration: 'none',
    },
    navLinks: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    navLink: {
      textDecoration: 'none',
      color: '#333',
      fontWeight: '500',
      transition: 'color 0.2s',
      cursor: 'pointer',
    },
    hero: {
      position: 'relative',
      height: 'auto',
      minHeight: '450px',
      background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), 
                  url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80') center/cover`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      textAlign: 'center',
      padding: '3rem 2rem 2rem 2rem',
    },
    heroContent: {
      maxWidth: '900px',
      width: '100%',
    },
    heroTitle: {
      fontSize: '3.5rem',
      fontWeight: '700',
      marginBottom: '0.75rem',
      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
    },
    heroSubtitle: {
      fontSize: '1.3rem',
      fontWeight: '300',
      marginBottom: '2rem',
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    },
    sectionHeader: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '2rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: 'white',
      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
    },
    sectionDescription: {
      fontSize: '1.1rem',
      color: 'rgba(255, 255, 255, 0.9)',
      marginBottom: '2rem',
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    },
    searchContainer: {
      maxWidth: '600px',
      margin: '0 auto 2rem',
    },
    searchForm: {
      display: 'flex',
      gap: '0',
      background: 'rgba(255, 56, 92, 0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '30px',
      overflow: 'hidden',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    },
    searchInput: {
      flex: 1,
      padding: '1rem 1.5rem',
      border: 'none',
      fontSize: '1rem',
      outline: 'none',
      background: 'transparent',
      color: 'white',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
    searchButton: {
      padding: '1rem 2rem',
      background: 'rgba(255, 56, 92, 0.8)',
      color: 'white',
      border: 'none',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '4rem 2rem',
    },
    storeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '2rem',
      marginBottom: '4rem',
    },
    storeCard: {
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '1px solid #e8e8e8',
    },
    storeImage: {
      height: '200px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative',
    },
    storeBadge: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      background: 'rgba(255, 255, 255, 0.9)',
      padding: '4px 8px',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '600',
      color: '#333',
    },
    storeInfo: {
      padding: '1.5rem',
    },
    storeName: {
      fontSize: '1.2rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#333',
    },
    storeDescription: {
      color: '#666',
      fontSize: '0.9rem',
      marginBottom: '1rem',
      lineHeight: '1.5',
      minHeight: '3rem',
    },
    storeStats: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.9rem',
    },
    storeProducts: {
      color: '#666',
    },
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.navContainer}>
          <Link to="/" style={styles.logo}>StoreToyou</Link>
          <nav style={styles.navLinks}>
            <Link to="/" style={styles.navLink}>Home</Link>
            <Link to="/dashboard" style={styles.navLink}>Dashboard</Link>
            <Link to="/login" style={styles.navLink}>Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section with Search and Filters */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>{formatCityName(cityName)}</h1>
          <p style={styles.heroSubtitle}>
            Discover amazing local businesses in the heart of the Okanagan Valley
          </p>

          {/* Search Bar */}
          <div style={styles.searchContainer}>
            <form style={styles.searchForm} onSubmit={handleSearchSubmit}>
              <input
                type="text"
                style={styles.searchInput}
                placeholder={`Search stores and products in ${formatCityName(cityName)}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button 
                type="submit" 
                style={styles.searchButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 56, 92, 1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 56, 92, 0.8)';
                }}
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11 2a9 9 0 1 0 9 9 9 9 0 0 0-9-9zm0 16a7 7 0 1 1 7-7 7 7 0 0 1-7 7z"/>
                  <path d="m21.71 20.29-4.6-4.6a.93.93 0 0 0-1.41 0 1 1 0 0 0 0 1.41l4.6 4.6a1 1 0 0 0 1.41 0 1 1 0 0 0 0-1.41z"/>
                </svg>
                Search
              </button>
            </form>
          </div>

          {/* Category Filters */}
          <CategoryFilterPills
            variant="dark"
            allLabel="All Stores"
            categories={categories}
            activeSlug={activeFilterSlug}
            onChange={handleFilterChange}
            resultLabel="stores"
          />

          {/* Scroll Down Arrow */}
          <div 
            style={{ 
              marginTop: '2rem', 
              cursor: 'pointer',
              position: 'relative',
              height: '40px'
            }}
            onClick={scrollToStores}
          >
            <div className="bounce-arrow" style={{ 
              display: 'inline-block',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}>
              <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
                <path 
                  d="M7 10l5 5 5-5" 
                  stroke="#FF385C" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M7 5l5 5 5-5" 
                  stroke="#FF385C" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  opacity="0.5"
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - Store Grid */}
      <main id="stores-section" style={styles.mainContent}>
        {/* Store Grid */}
        <div style={styles.storeGrid}>
          {stores.map((store, index) => (
            <div
              key={store.id}
              style={styles.storeCard}
              onClick={() => navigate(`/${cityName}/${store.url_name}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)';
              }}
            >
              <div 
                style={{
                  ...styles.storeImage,
                  backgroundImage: `url('${getStoreImage(store, index)}')`,
                }}
              >
                <div style={styles.storeBadge}>{getStoreCategory(store)}</div>
              </div>
              <div style={styles.storeInfo}>
                <div style={styles.storeName}>{store.name}</div>
                <div style={styles.storeDescription}>
                  {truncateText(store.description || 'Discover unique products and services from this local business.', 120)}
                </div>
                <div style={styles.storeStats}>
                  <div style={{ color: '#FF385C', fontWeight: '500' }}>
                    Visit Store →
                  </div>
                  <div style={styles.storeProducts}>
                    {getProductCount(store)} products
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {stores.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: '#666' }}>
            <h3>No stores found</h3>
            <p>Be the first to add your business in {formatCityName(cityName)}!</p>
            <Link to="/dashboard" className="btn btn-primary mt-3" style={{ backgroundColor: '#FF385C', borderColor: '#FF385C' }}>
              Add Your Store
            </Link>
          </div>
        )}
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <div 
          className="scroll-to-top"
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '50px',
            height: '50px',
            background: 'rgba(255, 56, 92, 0.9)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s ease',
            zIndex: 99,
          }}
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path 
              d="M7 14l5-5 5 5" 
              stroke="white" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .bounce-arrow {
          animation: bounce 2s infinite;
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          40% {
            transform: translateX(-50%) translateY(-10px);
          }
          60% {
            transform: translateX(-50%) translateY(-5px);
          }
        }
        
        /* Fixed scroll indicator at bottom right */
        .scroll-to-top {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          background: rgba(255, 56, 92, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
          z-index: 99;
        }
        
        .scroll-to-top:hover {
          background: #FF385C;
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }
        
        /* Style for search input placeholder */
        input::placeholder {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        
        /* Custom scrollbar for filter bar */
        .filter-bar::-webkit-scrollbar {
          height: 6px;
        }
        
        .filter-bar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .filter-bar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        
        .filter-bar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem !important;
          }
          
          .hero-subtitle {
            font-size: 1.1rem !important;
            margin-bottom: 1.5rem !important;
          }
          
          .section-title {
            font-size: 1.5rem !important;
          }
          
          .store-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important;
            gap: 1.5rem !important;
          }
          
          .main-content {
            padding: 3rem 1rem !important;
          }
          
          .nav-container {
            padding: 0 1rem !important;
          }
          
          .filter-bar {
            gap: 0.5rem !important;
          }
          
          .filter-btn {
            padding: 0.6rem 1rem !important;
            font-size: 0.85rem !important;
          }
          
          .hero {
            min-height: 400px !important;
            padding: 2rem 1rem 1.5rem 1rem !important;
          }
          
          .search-container {
            margin-bottom: 1.5rem !important;
          }
        }
        
        @media (max-width: 480px) {
          .hero-title {
            font-size: 2rem !important;
          }
          
          .hero-subtitle {
            font-size: 1rem !important;
            margin-bottom: 1.5rem !important;
          }
          
          .nav-links {
            gap: 1rem !important;
          }
          
          .nav-links a {
            font-size: 0.9rem;
          }
          
          .store-grid {
            grid-template-columns: 1fr !important;
          }
          
          .filter-bar {
            gap: 0.4rem !important;
            padding: 0.3rem 0 !important;
          }
          
          .filter-btn {
            padding: 0.5rem 0.9rem !important;
            font-size: 0.8rem !important;
          }
          
          .hero {
            min-height: auto !important;
            padding: 2rem 1rem 1rem 1rem !important;
          }
          
          .search-form {
            flex-direction: row !important;
          }
          
          .search-button {
            padding: 0.8rem 1.5rem !important;
          }
          
          .bounce-arrow {
            margin-top: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CityPage;
